import { isPlatformBrowser } from '@angular/common';
import {
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Inject,
  Input,
  OnDestroy,
  OnInit,
  Output,
  PLATFORM_ID,
} from '@angular/core';
import { FormBuilder } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import { AuthConfig, AUTH_CONFIG } from '@freelancer/auth';
import { Datastore, ResponseData } from '@freelancer/datastore';
import { UserRequiresGdprCollection } from '@freelancer/datastore/collections';
import { Facebook, FacebookUser } from '@freelancer/facebook';
import {
  AuthSuccessResponseAjax,
  LoginError,
  LoginSignupService,
  UserCheckError,
} from '@freelancer/login-signup';
import { ThreatmetrixService } from '@freelancer/threatmetrix';
import { ContainerSize } from '@freelancer/ui/container';
import { BackgroundColor } from '@freelancer/ui/page-layout';
import {
  maxLength,
  minLength,
  notEqualOtherControl,
  pattern,
  required,
  requiredTruthy,
  validEmailRegex,
  validUsernameRegex,
} from '@freelancer/ui/validators';
import { CookieService } from '@laurentgoudet/ngx-cookie';
import { RoleApi } from 'api-typings/common/common';
import { ErrorCodeApi } from 'api-typings/errors/errors';
import * as Rx from 'rxjs';
import { map, take } from 'rxjs/operators';
import { SuccessAction } from '../success-redirect/success-redirect.component';

enum SignupStep {
  DETAILS = 0,
  USERNAME_SELECT,
  ACCOUNT_TYPE,
  GDPR,
  FACEBOOK,
  CAPTCHA,
  SUCCESS,
  LOGIN_SUCCESS,
}

@Component({
  selector: 'app-signup',
  template: `
    <fl-card [ngSwitch]="currentStep">
      <app-details-form
        i18n="Create your Freelancer account"
        *ngSwitchCase="SignupStep.DETAILS"
        [facebookSignup]="true"
        [partnerDomain]="'Freelancer'"
        [facebookSdkLoaded]="
          facebookSdkPromise && (facebookSdkPromise | async) === true
        "
        [formGroup]="detailsFormGroup"
        [response]="detailsFormPromise && (detailsFormPromise | async)"
        (complete)="handleSubmitDetails()"
        (facebookClick)="handleFacebookSignupClick()"
        (switchToLogin)="handleSwitchToLogin()"
      ></app-details-form>
      <app-username-select-form
        *ngSwitchCase="SignupStep.USERNAME_SELECT"
        [email]="detailsFormGroup.get('email')?.value"
        [formGroup]="usernameFormGroup"
        [response]="signupPromise && (signupPromise | async)"
        (back)="handleBackAction()"
        (complete)="handleSubmitUsername()"
      ></app-username-select-form>
      <app-account-type-form
        *ngSwitchCase="SignupStep.ACCOUNT_TYPE"
        [formGroup]="accountTypeFormGroup"
        [response]="signupPromise && (signupPromise | async)"
        (back)="handleBackAction()"
        (complete)="handleSubmitAccountType()"
      ></app-account-type-form>
      <app-signup-gdpr-form
        *ngSwitchCase="SignupStep.GDPR"
        [formGroup]="gdprFormGroup"
        [response]="signupPromise && (signupPromise | async)"
        (back)="handleBackAction()"
        (complete)="handleSignup()"
      ></app-signup-gdpr-form>
      <app-facebook-signup
        *ngSwitchCase="SignupStep.FACEBOOK"
        [formGroup]="detailsFormGroup"
        [facebookUser]="facebookDetailsPromise | async"
        (complete)="handleSubmitFacebook()"
      ></app-facebook-signup>
      <app-captcha-form
        *ngSwitchCase="SignupStep.CAPTCHA"
        [formGroup]="captchaFormGroup"
        [response]="signupPromise && (signupPromise | async)"
        (back)="handleBackToStart()"
        (complete)="handleSignup()"
      ></app-captcha-form>
      <app-success-redirect
        *ngSwitchCase="SignupStep.SUCCESS"
        [action]="SuccessAction.SIGNUP"
      ></app-success-redirect>
      <!-- used for successful facebook login on the /signup route -->
      <app-success-redirect
        *ngSwitchCase="SignupStep.LOGIN_SUCCESS"
        [action]="SuccessAction.LOGIN"
      ></app-success-redirect>
    </fl-card>
  `,
})
export class SignupComponent implements OnInit, OnDestroy {
  BackgroundColor = BackgroundColor;
  ContainerSize = ContainerSize;

  SignupStep = SignupStep;
  SuccessAction = SuccessAction;

  currentStep = SignupStep.DETAILS;
  emailControl = this.fb.control(null, [
    required('Please enter an email address.'),
    pattern(validEmailRegex, 'Please enter a valid email address.'),
  ]);
  detailsFormGroup = this.fb.group(
    {
      email: this.emailControl,
      password: [
        null,
        [
          required('Please enter a password.'),
          minLength(6, 'Password must be at least 6 characters'),
          maxLength(150, 'Password must be at most 150 characters'),
          pattern(
            /([a-z].*[0-9A-Z])|([0-9A-Z].*[a-z])/,
            'Password must contain lowercase and uppercase letters or a number. ',
          ),
          notEqualOtherControl(
            this.emailControl,
            'Password cannot be your email.',
          ),
        ],
      ],
      termsOfUse: [
        null,
        [
          requiredTruthy(''), // Do not put an error message as it will not appear at the bottom of the <fl-label>
        ],
      ],
    },
    {
      updateOn: 'submit',
    },
  );
  usernameFormGroup = this.fb.group(
    {
      username: [
        null,
        [
          required('Please provide a username'),
          pattern(
            validUsernameRegex,
            'Username must be between 3 and 16 characters long and composed of only numbers and letters, starting with a letter.',
          ),
        ],
      ],
    },
    {
      updateOn: 'submit',
    },
  );
  accountTypeFormGroup = this.fb.group({
    accountType: [null, [required('Please select an account type')]],
  });
  gdprFormGroup = this.fb.group({
    privacyConsent: [false],
    personalUse: [false],
  });
  captchaFormGroup = this.fb.group({
    response: ['', required('Please fill out the CAPTCHA form')],
    challenge: [''],
  });

  detailsFormPromise: Promise<
    | ResponseData<unknown, UserCheckError>
    | ResponseData<AuthSuccessResponseAjax, LoginError>
  >;
  signupPromise?: Promise<ResponseData<AuthSuccessResponseAjax, string>>;

  /**
   * Facebook Auth promise via the Facebook SDK.
   * Returns the facebook auth details
   */
  facebookAuthPromise: Promise<ResponseData<fb.AuthResponse, fb.LoginStatus>>;
  facebookDetailsPromise: Promise<FacebookUser | undefined>;
  facebookSdkPromise: Promise<boolean>;

  private revalidateEmailPasswordSubscription?: Rx.Subscription;

  @Input() forceAccountType?: RoleApi;
  @Input() isModal = false;

  @Output() success = new EventEmitter<number>();
  @Output() switchToLogin = new EventEmitter();

  constructor(
    private activatedRoute: ActivatedRoute,
    @Inject(AUTH_CONFIG) private authConfig: AuthConfig,
    @Inject(PLATFORM_ID) private platformId: Object,
    private cookies: CookieService,
    private fb: FormBuilder,
    private loginSignup: LoginSignupService,
    private facebook: Facebook,
    private changeDetectorRef: ChangeDetectorRef,
    private datastore: Datastore,
    private threatmetrix: ThreatmetrixService,
  ) {}

  ngOnInit() {
    this.facebookSdkPromise = this.facebook.loadSdk();

    const savedState = this.loginSignup.getSavedState();
    if ('user' in savedState) {
      this.detailsFormGroup.controls.email.setValue(savedState.user);
      this.detailsFormGroup.controls.password.setValue(savedState.password);
    } else {
      this.facebookAuthPromise = savedState.facebookAuthPromise;
      this.facebookDetailsPromise = savedState.facebookDetailsPromise;
      this.setStep(SignupStep.FACEBOOK);
    }

    // Use URL Parameter if Email Address is set in the URL Parameter
    this.detailsFormGroup.controls.email.setValue(
      this.activatedRoute.snapshot.queryParams.email_address,
    );

    // force password control to revalidate when email is updated
    const emailControl = this.detailsFormGroup.controls.email;
    const passwordControl = this.detailsFormGroup.controls.password;
    this.revalidateEmailPasswordSubscription = emailControl.valueChanges.subscribe(
      _ => {
        if (passwordControl.touched && passwordControl.dirty) {
          passwordControl.updateValueAndValidity();
        }
      },
    );

    // This is a temporary solution to set the GDPR cookie.
    // We will move this to the logged out shell at
    // https://phabricator.tools.flnltd.com/T141066
    if (isPlatformBrowser(this.platformId) && this.authConfig.gdprCookie) {
      this.datastore
        .document<UserRequiresGdprCollection>('userRequiresGdpr')
        .valueChanges()
        .pipe(
          map(({ userRequiresGdpr }) => userRequiresGdpr),
          take(1),
        )
        .toPromise()
        .then(userRequiresGdpr => {
          if (this.authConfig.gdprCookie) {
            this.cookies.put(
              this.authConfig.gdprCookie,
              userRequiresGdpr.toString(),
            );
          }
        });
    }

    this.threatmetrix.load();
  }

  ngOnDestroy() {
    if (this.revalidateEmailPasswordSubscription) {
      this.revalidateEmailPasswordSubscription.unsubscribe();
    }
  }

  setStep(step: SignupStep) {
    this.currentStep = step;
    this.changeDetectorRef.markForCheck();
  }

  handleBackAction() {
    this.currentStep--;
  }

  handleBackToStart() {
    this.currentStep = SignupStep.DETAILS;
  }

  handleSubmitDetails() {
    this.detailsFormPromise = this.loginSignup.checkUserDetails({
      email: this.detailsFormGroup.value.email,
    });
    this.detailsFormPromise.then(response => {
      if (response.status === 'success') {
        this.setStep(SignupStep.USERNAME_SELECT);
      }
    });
  }

  handleSubmitFacebook() {
    this.setStep(SignupStep.USERNAME_SELECT);
  }

  handleSubmitUsername() {
    // username select step handles the checkUserDetails
    // TODO: move that validation logic into this OnInit for consistency?
    if (this.forceAccountType) {
      this.accountTypeFormGroup.patchValue({
        accountType: this.forceAccountType,
      });
      this.handleSignup();
    } else {
      this.setStep(SignupStep.ACCOUNT_TYPE);
    }
  }

  handleSubmitAccountType() {
    if (
      this.authConfig.gdprCookie &&
      this.cookies.get(this.authConfig.gdprCookie) === 'true'
    ) {
      // ask for privacy consent if user is European
      this.setStep(SignupStep.GDPR);
    } else {
      // assume privacy consent otherwise since user has no rights
      this.gdprFormGroup.patchValue({
        privacyConsent: true,
        personalUse: undefined,
      });
      this.handleSignup();
    }
  }

  handleSubmitGDPR() {
    this.handleSignup();
  }

  handleSignup() {
    if (this.facebookAuthPromise) {
      this.signupPromise = this.handleSignupWithFacebook();
      return;
    }

    this.signupPromise = this.loginSignup.signup({
      ...this.usernameFormGroup.value,
      ...this.detailsFormGroup.value,
      ...this.gdprFormGroup.value,
      captcha: this.captchaFormGroup.value,
      role: this.accountTypeFormGroup.value.accountType,
    });
    this.signupPromise.then(response => {
      if (response.status === 'success') {
        this.handleSuccess(response.result.user, response.result.token);
        return;
      }
      if (response.errorCode === ErrorCodeApi.SIGNUP_CAPTCHA_REQUIRED) {
        this.captchaFormGroup.patchValue({
          challenge: response.requestId,
        });
        this.signupPromise = undefined;
        this.setStep(SignupStep.CAPTCHA);
      }
    });
  }

  async handleSignupWithFacebook(): Promise<
    ResponseData<AuthSuccessResponseAjax, string>
  > {
    const facebookAuth = await this.facebookAuthPromise;
    const facebookDetails = await this.facebookDetailsPromise;
    if (facebookAuth.status === 'error' || !facebookDetails) {
      return {
        status: 'error',
        errorCode: ErrorCodeApi.AUTH_FACEBOOK_LINK_FAILED,
      };
    }

    const signupResponse = await this.loginSignup.signup({
      ...this.usernameFormGroup.value,
      ...this.gdprFormGroup.value,
      facebookAuth: facebookAuth.result,
      role: this.accountTypeFormGroup.value.accountType,
      email: this.emailControl.value,
    });

    if (signupResponse.status === 'success') {
      this.handleSuccess(
        signupResponse.result.user,
        signupResponse.result.token,
      );
    }
    return signupResponse;
  }

  handleSuccess(userId: number, authToken: string) {
    this.setStep(SignupStep.SUCCESS);

    this.loginSignup
      .handleSuccess('signup', userId, authToken, false, {
        redirectDisabled: this.isModal,
      })
      .then(() => this.success.emit(userId));
  }

  /**
   * Displays "Login Success!" and uses login redirect URL.
   * Called from a successful facebook login.
   */
  handleLoginSuccess(userId: number, authToken: string) {
    this.setStep(SignupStep.LOGIN_SUCCESS);
    this.loginSignup
      .handleSuccess('login', userId, authToken, false, {
        redirectDisabled: this.isModal,
      })
      .then(() => this.success.emit(userId));
  }

  handleSwitchToLogin() {
    this.loginSignup.saveState({
      user: this.detailsFormGroup.value.email,
      password: this.detailsFormGroup.value.password,
    });

    this.switchToLogin.emit();
  }

  handleFacebookSwitchToLogin() {
    // save facebook state and redirect to login to access link components
    this.loginSignup.saveState({
      facebookAuthPromise: this.facebookAuthPromise,
      facebookDetailsPromise: this.facebookDetailsPromise,
    });

    this.switchToLogin.emit();
  }

  handleFacebookSignupClick() {
    this.facebookAuthPromise = this.facebook.login();
    this.facebookDetailsPromise = this.facebookAuthPromise.then(response => {
      if (response.status === 'error') {
        return undefined;
      }
      return this.facebook.getUserDetails(response.result);
    });

    const facebookLoginPromise = this.facebookAuthPromise.then(response => {
      if (response.status === 'error') {
        return {
          status: 'error',
          errorCode: ErrorCodeApi.AUTH_FACEBOOK_LINK_FAILED,
        } as const;
      }
      return this.loginSignup.facebookLogin(response.result);
    });

    this.detailsFormPromise = facebookLoginPromise.then(async response => {
      if (response.status === 'success') {
        // this is a facebook login, so we use login success
        this.handleLoginSuccess(response.result.user, response.result.token);
      } else if (
        response.errorCode === ErrorCodeApi.AUTH_FACEBOOK_EMAIL_EXISTS
      ) {
        this.handleFacebookSwitchToLogin();
      } else if (
        response.errorCode === ErrorCodeApi.AUTH_USER_MISSING ||
        response.errorCode === ErrorCodeApi.FACEBOOK_EMAIL_NOT_FOUND
      ) {
        // user needs to signup
        this.setStep(SignupStep.FACEBOOK);
      }
      return response;
    });
  }
}
